import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaCheckCircle, FaLinkedinIn, FaRocket, FaUsers, FaBolt, FaAward } from 'react-icons/fa';
import SEO, { siteUrl } from '../components/SEO';

const About: React.FC = () => {

  const values = [
    {
      icon: <FaRocket className="text-5xl" />,
      title: 'Innovation',
      description: 'We stay ahead with cutting-edge technologies and creative solutions',
      color: 'from-red-500 to-orange-500'
    },
    {
      icon: <FaUsers className="text-5xl" />,
      title: 'Client-Focused',
      description: 'Your success is our priority, delivering beyond expectations',
      color: 'from-red-500 to-pink-500'
    },
    {
      icon: <FaAward className="text-5xl" />,
      title: 'Quality',
      description: 'Exceptional craftsmanship in every project we undertake',
      color: 'from-red-500 to-purple-500'
    },
    {
      icon: <FaBolt className="text-5xl" />,
      title: 'Speed',
      description: 'Fast delivery without compromising on quality',
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: '🎯',
      title: 'Precision',
      description: 'Attention to detail in every aspect of development',
      color: 'from-pink-500 to-red-500'
    },
    {
      icon: '🤝',
      title: 'Collaboration',
      description: 'Working together to achieve exceptional results',
      color: 'from-purple-500 to-red-500'
    }
  ];

  const whyChooseUs = [
    'Experienced team of developers and designers',
    'Proven track record of successful projects',
    'Agile development methodology',
    '24/7 launch support and maintenance',
    'Competitive pricing with no hidden costs',
    'Latest technology stack and best practices'
  ];

  const team = [
    {
      name: 'Akshay Bhojak',
      role: 'CEO & Founder',
      image: 'Akshay.webp',
      bio: 'Visionary leader with 13+ years in tech innovation',
      portfolio: 'https://akshaybhojak.redrender.in/',
      social: { linkedin: 'https://www.linkedin.com/in/akshay-bhojak-806576221?utm_source=share_via&utm_content=profile&utm_medium=member_android' }
    }
  ];

  const companyStats = [
    { number: '100+', label: 'Successful Projects', icon: '🚀' },
    { number: '50+', label: 'Happy Clients', icon: '😊' }
  ];

  const stats = [
    { number: '13+', label: 'Years overall tech career', icon: '💻' },
    { number: '8+', label: 'Years in 3D & AR/VR', icon: '🕶️' },
    { number: '6+', label: 'Years Unity & Studio lead', icon: '🎮' },
    { number: '100+', label: 'Projects shaped with RedRender', icon: '🚀' }
  ];

  const aboutJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'RedRender',
      url: siteUrl,
      logo: `${siteUrl}/logo.png`,
      description: 'RedRender is a digital development company building web apps, mobile apps, games, AR/VR experiences, and 3D visualization products.'
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Akshay Bhojak',
      jobTitle: 'CEO & Founder',
      worksFor: {
        '@type': 'Organization',
        name: 'RedRender',
        url: siteUrl
      },
      url: 'https://akshaybhojak.redrender.in/',
      sameAs: [
        'https://akshaybhojak.redrender.in/',
        'https://www.linkedin.com/in/akshay-bhojak-806576221/'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black pt-24 pb-20 relative overflow-hidden">
      <SEO
        title="About RedRender - Digital Development Team & Founder"
        description="Learn about RedRender, a digital development company led by Akshay Bhojak, building web apps, mobile apps, games, AR/VR experiences, and 3D visualization products."
        path="/about"
        image="/Akshay.webp"
        keywords={[
          'About RedRender',
          'Akshay Bhojak',
          'RedRender founder',
          'development team',
          'AR VR development company'
        ]}
        jsonLd={aboutJsonLd}
      />
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:100px_100px] pointer-events-none"></div>
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-red-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-float pointer-events-none"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Page Header */}
        <div className="text-center mb-16 sm:mb-20 animate-slide-up">
          <div className="inline-flex items-center space-x-2 bg-red-500/10 border border-red-500/30 rounded-full px-5 py-2 backdrop-blur-sm mb-6">
            <span className="text-red-500 font-semibold text-sm font-display">About Us</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-display">
            About <span className="bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent">RedRender</span>
          </h1>
          <p className="text-gray-400 text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed">
            We are a passionate team dedicated to turning your digital dreams into reality
          </p>
        </div>

        {/* Main Story Section */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
          <div className="space-y-6 animate-slide-up">
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-display leading-tight">
              Crafting Digital Excellence <span className="text-red-500">Since Day One</span>
            </h2>
            <div className="space-y-4 text-gray-400 text-base sm:text-lg leading-relaxed">
              <p>
                RedRender is a premier service-based company specializing in comprehensive digital solutions. 
                From web and mobile applications to immersive games and AR/VR experiences, we bring innovation 
                and creativity to every project. Our expertise extends to stunning visual designs including logos, 
                visiting cards, and complete brand identities.
              </p>
              <p>
                We believe in the power of technology to transform businesses. Our team combines technical 
                excellence with creative vision to deliver solutions that not only meet but exceed expectations. 
                Whether you're a startup or an established enterprise, we're here to elevate your digital presence.
              </p>
            </div>
            
            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-4 pt-6">
              {companyStats.map((stat, index) => (
                <div
                  key={index}
                  className="group relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl p-6 sm:p-8 rounded-2xl border border-white/10 hover:border-red-500/50 hover:scale-105 transition-all duration-300"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                  <div className="relative">
                    <div className="text-3xl mb-2">{stat.icon}</div>
                    <h4 className="text-4xl sm:text-5xl font-bold text-red-500 mb-2 font-display">{stat.number}</h4>
                    <p className="text-white/90 font-medium">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative group animate-scale-in">
            <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 to-red-500/0 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
            <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-3xl p-8 sm:p-10 border border-white/10 hover:border-red-500/30 transition-all duration-500">
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-8 font-display">Why Choose Us?</h3>
              <div className="space-y-4">
                {whyChooseUs.map((reason, index) => (
                  <div 
                    key={index} 
                    className="flex items-start space-x-4 group/item hover:translate-x-2 transition-transform duration-300"
                  >
                    <div className="flex-shrink-0 mt-1">
                      <FaCheckCircle className="text-red-500 text-xl group-hover/item:scale-125 transition-transform" />
                    </div>
                    <p className="text-gray-300 group-hover/item:text-white transition-colors leading-relaxed">{reason}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-20 animate-slide-up">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="group relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-red-500/50 transition-all duration-300 text-center hover:-translate-y-2"
            >
              <div className="text-4xl mb-3">{stat.icon}</div>
              <h3 className="text-3xl sm:text-4xl font-bold text-red-500 mb-2 font-display group-hover:scale-110 transition-transform">
                {stat.number}
              </h3>
              <p className="text-gray-400">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Values Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-display">
              Our <span className="text-red-500">Core Values</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="group relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-3xl p-8 border border-white/10 hover:border-red-500/50 transition-all duration-500 text-center hover:-translate-y-2 hover:shadow-2xl hover:shadow-red-500/20 animate-scale-in overflow-hidden"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${value.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                <div className="relative">
                  <div className="text-red-500 mb-6 inline-block group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    {typeof value.icon === 'string' ? (
                      <span className="text-5xl sm:text-6xl">{value.icon}</span>
                    ) : (
                      value.icon
                    )}
                  </div>
                  <h4 className="text-xl sm:text-2xl font-bold text-white mb-3 font-display">{value.title}</h4>
                  <p className="text-gray-400 leading-relaxed">{value.description}</p>
                </div>
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-500/20 to-transparent rounded-bl-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Team Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-display">
              Meet Our <span className="text-red-500">Amazing Team</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Talented professionals dedicated to bringing your vision to life
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div
                key={index}
                className="group relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-3xl overflow-hidden border border-white/10 hover:border-red-500/50 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-red-500/20 animate-scale-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-red-500/0 to-red-500/0 group-hover:from-red-500/10 group-hover:to-red-500/5 transition-all duration-500"></div>
                
                {/* Image */}
                <div className="relative h-80 overflow-hidden">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  
                  {/* Social Links Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <a
                      href={member.social.linkedin}
                      className="p-3 bg-red-500 rounded-full text-white hover:bg-red-600 transition-all duration-300 transform hover:scale-110"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <FaLinkedinIn />
                    </a>
                  </div>
                </div>

                {/* Content */}
                <div className="relative p-6">
                  <h3 className="text-2xl font-bold text-white mb-1 font-display group-hover:text-red-400 transition-colors">
                    {member.name}
                  </h3>
                  <p className="text-red-500 font-medium mb-3">{member.role}</p>
                  <p className="text-gray-400 text-sm leading-relaxed">{member.bio}</p>
                  <a
                    href={member.portfolio}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-5 inline-flex items-center rounded-full border border-red-500/40 bg-red-500/10 px-5 py-2.5 text-sm font-semibold text-red-300 transition-all duration-300 hover:border-red-500 hover:bg-red-500 hover:text-white hover:shadow-lg hover:shadow-red-500/30"
                  >
                    View Portfolio
                    <FaArrowRight className="ml-2 text-xs" />
                  </a>
                </div>

                {/* Corner Accent */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-3xl p-12 border border-white/10 animate-slide-up">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-display">
            Ready to start your project with <span className="text-red-500">us?</span>
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help transform your ideas into exceptional digital experiences
          </p>
          <Link
            to="/contact"
            className="inline-block relative px-8 py-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-full font-semibold overflow-hidden group shadow-lg hover:shadow-red-500/50 transition-all duration-300"
          >
            <span className="relative z-10">Let's Talk</span>
            <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-red-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default About;
